/**
 * <toppik-footer>
 * On mobile the menu columns collapse into accordions; on desktop they are
 * always open and their headers are inert. Column bodies stay in the DOM; we
 * just toggle [hidden] + aria-expanded so the layout can switch cleanly at the
 * breakpoint.
 */
class ToppikFooter extends HTMLElement {
  connectedCallback() {
    this.mq = window.matchMedia('(max-width: 749px)');
    this.columns = Array.from(this.querySelectorAll('[data-collapsible]'));

    this.columns.forEach((col) => {
      const heading = col.querySelector('.toppik-footer__heading');
      const body = col.querySelector('.toppik-footer__col-body');
      if (!heading || !body) return;
      if (!heading.dataset.wired) {
        heading.dataset.wired = '1';
        heading.addEventListener('click', () => {
          if (!this.mq.matches) return; // desktop: no toggling
          const open = heading.getAttribute('aria-expanded') !== 'true';
          heading.setAttribute('aria-expanded', String(open));
          body.hidden = !open;
        });
      }
    });

    this.apply();
    this.mq.addEventListener?.('change', () => this.apply());

    this.setupQuotes();
  }

  setupQuotes() {
    const container = this.querySelector('[data-footer-quotes]');
    if (!container) return;
    const quotes = Array.from(container.querySelectorAll('.toppik-footer__quote-text'));
    if (!quotes.length) return;

    // Press logos: buttons that jump to their matching quote; the active one
    // stays bright (highlight applied to the image element via data-active).
    const logosWrap = this.querySelector('[data-footer-logos]');
    const logoBtns = logosWrap ? Array.from(logosWrap.querySelectorAll('.toppik-footer__press-logo-btn')) : [];
    const logoImgs = logosWrap ? Array.from(logosWrap.querySelectorAll('.toppik-footer__press-logo')) : [];
    if (logoImgs.length) logosWrap.setAttribute('data-linked', '');

    let index = quotes.findIndex((q) => q.hasAttribute('data-active'));
    if (index < 0) index = 0;

    const syncLogos = () => {
      if (!logoImgs.length) return;
      const slot = quotes[index]?.dataset.slot;
      logoImgs.forEach((logo) => logo.toggleAttribute('data-active', logo.dataset.slot === slot));
    };

    const showIndex = (next) => {
      quotes[index]?.removeAttribute('data-active');
      index = (next + quotes.length) % quotes.length;
      quotes[index]?.setAttribute('data-active', '');
      syncLogos();
    };

    // Jump to the quote whose slot matches the given value (a logo's slot).
    const showSlot = (slot) => {
      const target = quotes.findIndex((q) => q.dataset.slot === String(slot));
      if (target > -1) showIndex(target);
    };

    syncLogos();

    const interval = (Number(container.dataset.interval) || 6) * 1000;
    let timer = null;
    const startTimer = () => {
      window.clearInterval(timer);
      if (quotes.length < 2) return;
      timer = window.setInterval(() => showIndex(index + 1), interval);
    };
    startTimer();

    // Click a press logo -> show its quote and restart the rotation.
    logoBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        showSlot(btn.dataset.slot);
        startTimer();
      });
    });

    // Clicking the quote (the news) opens that quote's article link in a new
    // tab. If the active quote has no link set, fall back to advancing so the
    // click is never a dead end. The rotation keeps running either way.
    const trigger = this.querySelector('[data-footer-quote]') || container;
    trigger.addEventListener('click', () => {
      const link = quotes[index]?.dataset.link;
      if (link) {
        window.open(link, '_blank', 'noopener');
      } else {
        showIndex(index + 1);
        startTimer();
      }
    });
  }

  apply() {
    const mobile = this.mq.matches;
    this.columns.forEach((col) => {
      const heading = col.querySelector('.toppik-footer__heading');
      const body = col.querySelector('.toppik-footer__col-body');
      if (!heading || !body) return;
      // Desktop: everything open. Mobile: open by default (matches the design).
      heading.setAttribute('aria-expanded', 'true');
      body.hidden = false;
      void mobile;
    });
  }
}

if (!customElements.get('toppik-footer')) {
  customElements.define('toppik-footer', ToppikFooter);
}
